import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { CustomerService } from '../../features/customers/services/CustomerService';

const SearchAutocomplete = ({
    placeholder = "Search...",
    className = "",
    onSearchSubmit,
    variant = "desktop" // 'desktop' | 'mobile'
}) => {
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const wrapperRef = useRef(null);

    // Debounce Search
    useEffect(() => {
        const timer = setTimeout(async () => {
            if (query.trim().length >= 1) {
                setLoading(true);
                try {
                    // Use the optimized search service
                    const matches = await CustomerService.searchCustomers(query);
                    setResults(matches.slice(0, 5)); // Limit to top 5
                    setShowResults(true);
                } catch (error) {
                    console.error("Autocomplete search failed", error);
                } finally {
                    setLoading(false);
                }
            } else {
                setResults([]);
                setShowResults(false);
            }
        }, 300); // 300ms debounce

        return () => clearTimeout(timer);
    }, [query]);

    // Click Outside to Close
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowResults(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSelect = (customer) => {
        const safeMobile = customer.primaryMobile.replace(/[.#$[\]/]/g, '_');
        navigate(`/customers/${safeMobile}`);
        setShowResults(false);
        setQuery('');
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            if (onSearchSubmit) {
                onSearchSubmit(query);
            } else {
                navigate(`/customers?search=${encodeURIComponent(query)}`);
            }
            setShowResults(false);
        }
    };

    return (
        <div ref={wrapperRef} className={`relative ${className}`}>
            <div className="relative">
                <Search
                    className={`absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 ${variant === 'mobile' ? 'w-4 h-4' : 'w-5 h-5'}`}
                />
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    className={`w-full pl-10 pr-10 ${variant === 'mobile' ? 'py-2 text-sm' : 'py-2'} bg-slate-100 border-none rounded-full focus:ring-2 focus:ring-purple-100 outline-none transition-all`}
                />
                {loading && (
                    <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 text-purple-500 animate-spin w-4 h-4" />
                )}
                {!loading && query && (
                    <button
                        onClick={() => { setQuery(''); setResults([]); }}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                        <X size={16} />
                    </button>
                )}
            </div>

            {/* Results Dropdown */}
            {showResults && results.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-slate-100 overflow-hidden z-50">
                    {results.map((customer) => (
                        <div
                            key={customer.primaryMobile}
                            onClick={() => handleSelect(customer)}
                            className="p-3 hover:bg-slate-50 cursor-pointer border-b border-slate-50 last:border-none flex justify-between items-center"
                        >
                            <div>
                                <p className="font-medium text-slate-800 text-sm">{customer.name}</p>
                                <p className="text-xs text-slate-500">{customer.primaryMobile}</p>
                            </div>
                            <span className={`text-[10px] px-2 py-1 rounded-full font-bold ${(Number(customer.healthScore) || 0) > 80 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                {customer.healthScore || 0}
                            </span>
                        </div>
                    ))}
                    <div
                        onClick={() => handleKeyDown({ key: 'Enter' })}
                        className="p-2 text-center text-xs font-bold text-purple-600 bg-purple-50 hover:bg-purple-100 cursor-pointer"
                    >
                        View all results for "{query}"
                    </div>
                </div>
            )}

            {showResults && query.length >= 1 && results.length === 0 && !loading && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-slate-100 p-4 text-center z-50">
                    <p className="text-sm text-slate-500">No customers found</p>
                </div>
            )}
        </div>
    );
};

export default SearchAutocomplete;
